import React, { useState } from 'react';
import config from 'src/config';
import { useSearchParams } from 'react-router-dom';
import { CircleFadingArrowUp, Pencil, MapPinPen, Target, CalendarRange, LandPlot, Trees, Plus, LineChart } from 'lucide-react';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { countryNameToFlagEmoji } from 'src/shared/data/countries';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import Modal from 'src/shared/components/modal/Modal';
import Select, { SelectOption } from 'src/shared/components/select/Select';
import Input from 'src/shared/components/input/Input';
import ReadMore from 'src/shared/components/read-more/ReadMore';
import Textarea from 'src/shared/components/textarea/Textarea';
import { FeatureType, Resources } from 'src/shared/types';
import useFeature from 'src/shared/hooks/useFeature';
import useProject from 'src/shared/hooks/useProject';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import Tooltip from 'src/shared/components/tooltip/Tooltip';
import SiteFileAssets from './SiteFileAssets';
import {
    useSiteContextFieldsQuery,
    useSetSiteActivityMutation,
    useSetSiteStageMutation,
    useSetSiteObjectivesMutation,
    useSetSiteAreaMutation,
    useSetSiteEcologicalContextMutation,
} from '../state/api/siteSummaryGraphSlice';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

const ContextCard = ({
    icon,
    action,
    title,
    value,
    onEdit,
    isPrimary = false,
    className,
}: {
    icon?: React.ReactNode;
    action?: React.ReactNode;
    title: string;
    value: React.ReactNode;
    onEdit?: () => void;
    isPrimary?: boolean;
    className?: string;
}) => (
    <div
        className={`relative flex-1 flex self-stretch flex-col rounded-lg shadow-portfolio-summary-card gap-1 p-4 ${
            isPrimary ? styles.contextCardPrimary : styles.contextCardSecondary
        } ${className || ''}`}
    >
        <div className='flex items-center justify-between'>
            <div className='flex gap-2 items-center'>
                {icon && <div className={styles.iconGradient}>{icon}</div>}
                <div className='text-[17px] text-primary'>{title}</div>
            </div>
            <div className='flex gap-2 items-center'>
                {onEdit && (
                    <Tooltip content='Edit content'>
                        <SecondaryButton
                            className='text-primary text-sm flex items-center gap-1 border-grey-40 p-1 h-auto'
                            onClick={onEdit}
                        >
                            <Pencil className='h-4 w-4' />
                        </SecondaryButton>
                    </Tooltip>
                )}
                {action}
            </div>
        </div>
        <div className='flex text-sm text-black mt-1 mb-2 flex-1'>{value}</div>
    </div>
);

// Shared ReadMore is imported above

type EditDetails = {
    field: 'activity' | 'stage' | 'objectives' | 'area' | 'ecologicalContext';
    value: string;
};

const SiteContext = () => {
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId') || '';
    const { currentData: contextFields } = useSiteContextFieldsQuery({ projectId: projectId });
    const navigate = useAppNavigation();
    const { trackEditButtonClick, trackExploreButtonClick, trackUpgradeSubscriptionIconClick } = useSiteSummaryAnalytics();

    const [editing, setEditing] = useState<null | EditDetails>(null);

    const [setActivity] = useSetSiteActivityMutation();
    const [setStage] = useSetSiteStageMutation();
    const [setObjectives] = useSetSiteObjectivesMutation();
    const [setArea] = useSetSiteAreaMutation();
    const [setEcologicalContext] = useSetSiteEcologicalContextMutation();

    const handleAddEditClick = (editDetails: EditDetails) => {
        setEditing({ field: editDetails.field, value: editDetails.value });
        trackEditButtonClick({ section: editDetails.field });
    };

    const EmptyState = ({ title, buttonLabel, onClick }: { title: string; buttonLabel: string; onClick: () => void }) => (
        <div className='flex items-center h-[130px] w-full'>
            <div className='flex flex-col gap-2 text-center w-full'>
                <div className='text-2xl text-black'>{title}</div>
                <div className='text-sm text-grey-80 mb-2'>This will be seen by anyone with access to this project</div>
                <div className='w-auto self-center'>
                    <PrimaryButton className='flex gap-2' onClick={onClick}>
                        {buttonLabel} <Plus className='text-white' />
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );

    // Derive Ecology Hub redirection link
    const { currentProjectFilters } = useProjectFilters();
    const { hasFeature, hasResource } = useFeature();
    const isEdnaProject = hasFeature({
        feature: FeatureType.EDNA,
    });
    const hasEcologyHubResource = hasResource({
        resource: Resources.ECO_STATS,
    });
    const { isHabitatInsightsProject } = useProject();
    const hasAnyEdnaSource = currentProjectFilters?.habitatAssays?.some(entry => entry.source === 'eDNA');
    const isAllSingleTimepoint = currentProjectFilters?.habitatAssays?.every(entry => entry.singleTimepoint);

    let isEcologyHubAvailable = true;
    let ecologyHubRedirect: () => void;
    if (isHabitatInsightsProject) {
        ecologyHubRedirect = navigate.toHabitatTrends;
    } else if (hasEcologyHubResource && !isAllSingleTimepoint && hasAnyEdnaSource) {
        ecologyHubRedirect = navigate.toSpeciesTrends;
    } else if (hasEcologyHubResource && isEdnaProject && hasAnyEdnaSource) {
        ecologyHubRedirect = navigate.toSampleComparison;
    } else {
        isEcologyHubAvailable = false;
    }

    const exploreEcologyHubClick = () => {
        trackExploreButtonClick({ section: 'ecological_context' });
        ecologyHubRedirect();
    };

    return (
        <div className='flex flex-col gap-3'>
            <div className={`text-xl text-black ${styles.sectionTitle}`}>My site</div>

            <div className='flex-1 flex flex-col gap-3'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3'>
                    <ContextCard
                        title='Stage'
                        value={contextFields?.stage || 'Select Site Stage'}
                        onEdit={() => handleAddEditClick({ field: 'stage', value: contextFields?.stage || '' })}
                    />
                    <ContextCard
                        title='Area'
                        value={contextFields?.area ? `${contextFields.area.toLocaleString()} km²` : '-- km²'}
                        onEdit={() => handleAddEditClick({ field: 'area', value: String(contextFields?.area ?? '') })}
                    />
                    <ContextCard
                        title='Country'
                        value={
                            <span>
                                {contextFields?.country || '-'} {contextFields?.country && countryNameToFlagEmoji(contextFields.country)}
                            </span>
                        }
                    />
                    <ContextCard
                        title='Subscription'
                        value={contextFields?.subscription || '—'}
                        action={
                            <Tooltip content={'Upgrade subscription to Species Insights'}>
                                <a
                                    rel='noreferrer'
                                    href={config.bookDemoCallFormUrl}
                                    target='_blank'
                                    onClick={trackUpgradeSubscriptionIconClick}
                                >
                                    <CircleFadingArrowUp className='text-primary' />
                                </a>
                            </Tooltip>
                        }
                    />
                </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                <ContextCard
                    icon={<Target className='text-primary' />}
                    title='Objectives'
                    isPrimary={true}
                    value={
                        (contextFields?.objectives || '').trim() ? (
                            <ReadMore text={(contextFields?.objectives || '').trim()} lines={7} modalTitle='Objectives' />
                        ) : (
                            <EmptyState
                                title='Add your objective'
                                buttonLabel='Add objective'
                                onClick={() => handleAddEditClick({ field: 'objectives', value: '' })}
                            />
                        )
                    }
                    onEdit={
                        contextFields?.objectives
                            ? () => handleAddEditClick({ field: 'objectives', value: contextFields?.objectives || '' })
                            : undefined
                    }
                />

                <ContextCard
                    icon={<MapPinPen className='text-primary' />}
                    title='Type of site and activity'
                    isPrimary={true}
                    value={
                        (contextFields?.activity || '').trim() ? (
                            <ReadMore text={(contextFields?.activity || '').trim()} lines={7} modalTitle='Type of site and activity' />
                        ) : (
                            <EmptyState
                                title='Add type of site and activity'
                                buttonLabel='Add details'
                                onClick={() => handleAddEditClick({ field: 'activity', value: '' })}
                            />
                        )
                    }
                    onEdit={
                        contextFields?.activity
                            ? () => handleAddEditClick({ field: 'activity', value: contextFields?.activity || '' })
                            : undefined
                    }
                />

                <ContextCard
                    icon={<Trees className='text-primary' />}
                    title='Ecological context'
                    isPrimary={true}
                    className='md:col-span-2 lg:col-span-1'
                    value={
                        (contextFields?.ecologicalContext || '').trim() ? (
                            <ReadMore text={(contextFields?.ecologicalContext || '').trim()} lines={7} modalTitle='Ecological context' />
                        ) : (
                            <EmptyState
                                title='Add ecological context'
                                buttonLabel='Add context'
                                onClick={() => handleAddEditClick({ field: 'ecologicalContext', value: '' })}
                            />
                        )
                    }
                    onEdit={
                        contextFields?.ecologicalContext
                            ? () => handleAddEditClick({ field: 'ecologicalContext', value: contextFields?.ecologicalContext || '' })
                            : undefined
                    }
                    action={
                        isEcologyHubAvailable && (
                            <Tooltip content={'Explore the Ecology Hub'}>
                                <a className='cursor-pointer' rel='noreferrer' onClick={exploreEcologyHubClick}>
                                    <LineChart className='text-primary' />
                                </a>
                            </Tooltip>
                        )
                    }
                />
            </div>

            <SiteFileAssets projectId={projectId} />

            {editing && editing.field !== 'stage' && (
                <EditFieldModal
                    field={editing.field}
                    value={editing.value}
                    onClose={() => setEditing(null)}
                    onSave={async newValue => {
                        const trimmed = newValue.trim();
                        switch (editing.field) {
                            case 'activity':
                                await setActivity({ projectId: projectId, activity: trimmed }).unwrap();
                                break;
                            case 'objectives':
                                await setObjectives({ projectId: projectId, objectives: trimmed }).unwrap();
                                break;
                            case 'area': {
                                const areaNumber = Number(trimmed);
                                if (!Number.isNaN(areaNumber)) {
                                    await setArea({ projectId: projectId, area: areaNumber }).unwrap();
                                }
                                break;
                            }
                            case 'ecologicalContext':
                                await setEcologicalContext({ projectId: projectId, ecologicalContext: trimmed }).unwrap();
                                break;
                        }
                        setEditing(null);
                    }}
                />
            )}
            {editing && editing.field === 'stage' && (
                <StageEditModal
                    initialValue={editing.value}
                    onClose={() => setEditing(null)}
                    onSave={async newValue => {
                        const trimmed = newValue.trim();
                        await setStage({ projectId: projectId, stage: trimmed }).unwrap();
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
};

export default SiteContext;

const StageEditModal = ({
    initialValue,
    onClose,
    onSave,
}: {
    initialValue: string;
    onClose: () => void;
    onSave: (newValue: string) => Promise<void>;
}) => {
    const stageOptions = [
        'Exploration / Planning',
        'Project development',
        'Production / Operations',
        'Decommissioning / Closure',
        'Restoration',
        'Other',
    ] as const;
    const isInitialKnown = stageOptions.includes(initialValue as any);
    const [selected, setSelected] = useState<string>(isInitialKnown ? initialValue : 'Other');
    const [other, setOther] = useState<string>(!isInitialKnown ? initialValue : '');

    const selectOptions: SelectOption[] = stageOptions.map(s => ({ value: s, label: s }));
    const resolvedValue = selected === 'Other' ? other : selected;

    return (
        <Modal classNames={{ body: 'w-[700px] p-6' }} onClose={onClose} dismissOnClickOutside={true}>
            <div className='flex gap-3 flex-col'>
                <div className='flex items-center gap-2 text-xl text-primary'>
                    <CalendarRange className='text-primary' />
                    Enter stage of site
                </div>
                <div className='text-sm text-grey-80'>&lt;instructions here for entering site stage site here&gt;</div>

                <div className='flex flex-col gap-1'>
                    <div className='text-primary text-sm'>Stage</div>
                    <Select
                        options={selectOptions}
                        onChange={val => setSelected(val)}
                        selectedValue={selected}
                        placeholder='Select stage'
                        width={'100%'}
                    />
                </div>

                {selected === 'Other' && (
                    <div className='flex flex-col gap-1'>
                        <div className='text-sm'>If other, please specify</div>
                        <Input placeholder='Type stage here' value={other} onChange={e => setOther(e.target.value)} />
                    </div>
                )}

                <PrimaryButton className='self-end gap-3' onClick={() => onSave(resolvedValue)}>
                    Save
                </PrimaryButton>
            </div>
        </Modal>
    );
};

const EditFieldModal = ({
    field,
    value,
    onClose,
    onSave,
}: {
    field: 'activity' | 'objectives' | 'area' | 'ecologicalContext';
    value: string;
    onClose: () => void;
    onSave: (newValue: string) => Promise<void>;
}) => {
    const isNumber = field === 'area';
    // set numeric '0' to empty string to display the place holder
    const defaultValue = isNumber && value === '0' ? '' : value;
    const [val, setVal] = useState<string>(defaultValue || '');
    const isLongText = field === 'objectives' || field === 'ecologicalContext' || field === 'activity';

    const titleMap: Record<typeof field, string> = {
        activity: 'Edit activity',
        objectives: 'Edit objectives',
        area: 'Edit area (km²)',
        ecologicalContext: 'Edit ecological context',
    } as const;

    const iconMap: Record<typeof field, React.ReactNode> = {
        activity: <MapPinPen className='text-primary' />,
        objectives: <Target className='text-primary' />,
        area: <LandPlot className='text-primary' />,
        ecologicalContext: <Trees className='text-primary' />,
    } as const;

    return (
        <Modal classNames={{ body: 'w-[800px] p-6' }} onClose={onClose} dismissOnClickOutside={true}>
            <div className='flex gap-3 flex-col'>
                <div className='flex items-center gap-2 text-xl text-primary'>
                    {iconMap[field]}
                    {titleMap[field]}
                </div>
                {field === 'activity' && (
                    <div className='text-sm'>
                        Add which type of site and activity for your site, e.g.
                        <br />
                        <ul className='list-disc pl-4'>
                            <li className='pl-1'>Mine site adjacent to a fragile national park savannah ecosystem.</li>
                            <li className='pl-1'>
                                This is the development of an offshore wind farm off the coast of Portugal. This will include activity that
                                will disturb the seafloor in setting up the windfarm, the extension of a port area to support their
                                construction and maintenance and a cable route to transfer energy onshore. As a form of biodiversity
                                offsetting, a seaweed farm is being created. The overall impact of these activities is being measured
                                against a control area.
                            </li>
                            <li className='pl-1'>
                                At a site in Puerto Galan, intense fishing resulted in an MPA being established. This project covered a
                                coral reef area, with other areas of coral reef and mangroves around the MPA.
                            </li>
                        </ul>
                        <br />
                        <br />
                        You can edit this site activity at any time.
                    </div>
                )}
                {field === 'objectives' && (
                    <div className='text-sm'>
                        Add your objectives for this site, e.g.
                        <br />
                        <ul className='list-disc pl-4'>
                            <li className='pl-1'>
                                The primary objectives of the operational &apos;Zvimba Mine&apos; eDNA project are to non-invasively monitor
                                and assess the ecological health of the local aquatic ecosystems. This is to be achieved by analyzing
                                genetic material from water samples to establish a baseline of existing biodiversity. The project also aims
                                to track changes in species composition over time, identify and monitor the presence and distribution of key
                                species.
                            </li>
                            <li className='pl-1'>
                                The aim is to baseline and monitor progress towards a biodiversity net positive impact of an offshore
                                windfarm development. This includes tracking changes in species composition over time, identify and monitor
                                the presence and distribution of key species.
                            </li>
                            <li className='pl-1'>
                                Compare the effectiveness of MPAs for reef fish diversity. Compare mangrove and reef fish communities.
                                Baseline the fish diversity in the area.
                            </li>
                        </ul>
                        <br />
                        <br />
                        You can edit this objective at any time.
                    </div>
                )}
                {field === 'ecologicalContext' && (
                    <div className='text-sm'>
                        Add the ecological context for your site, e.g.
                        <br />
                        <ul className='list-disc pl-4'>
                            <li className='pl-1'>
                                The Zvimba Mine is located in the Zvimba District of Zimbabwe, an area characterized by a humid subtropical
                                climate and a landscape of agricultural lands interspersed with miombo woodlands and savanna. The
                                region&apos;s natural ecosystems, particularly its crucial seasonal and perennial waterways, are under
                                significant environmental stress from extensive human activities, including gold mining.
                            </li>
                            <li className='pl-1'>
                                Offshore Portugal is characterised by diverse north-east Atlantic marine fauna, with different communities
                                present in surface and benthic waters.
                            </li>
                            <li className='pl-1'>This project compares two reefs – inside and outside of an MPA – and nearby mangrove.</li>
                        </ul>
                        <br />
                        <br />
                        You can edit this ecological context at any time.
                    </div>
                )}
                {isLongText ? (
                    <Textarea
                        className='h-[200px]'
                        value={val}
                        onChange={e => setVal(e.target.value)}
                        maxLength={800}
                        placeholder={`Type ${
                            field === 'activity' ? 'site activity' : field === 'objectives' ? 'objectives' : 'ecological context'
                        } here`}
                    />
                ) : (
                    <input
                        className='border rounded p-2 h-[40px]'
                        type={isNumber ? 'number' : 'text'}
                        value={val}
                        onChange={e => setVal(e.target.value)}
                        placeholder={`Type ${field} here`}
                        min={isNumber ? 1 : undefined}
                    />
                )}

                <PrimaryButton className='self-end gap-3' onClick={() => onSave(val)}>
                    Save
                </PrimaryButton>
            </div>
        </Modal>
    );
};
